import React from "react";
import { AlertTriangle, Rocket, X } from "lucide-react";
import { PaStatusBadge, PA_TIMELINE, paDate, paMoney } from "./providerAutomationStatus";

/* Modal de confirmación de recarga real (el admin autenticado ES el operador). */
export const PaConfirmModal = ({ operation, busy, onCancel, onConfirm }) => {
  if (!operation) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="pa-confirm-modal">
      <div className="w-full max-w-md bg-ag7-surface border border-amber-400/40 rounded-2xl p-6 space-y-4 shadow-[0_0_40px_-10px_rgba(245,158,11,0.6)] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-300 flex-shrink-0" />
          <div>
            <h3 className="font-heading font-black text-white uppercase tracking-wide">Confirmar recarga real</h3>
            <p className="text-xs text-[var(--ag7-text-muted)]">Esta acción enviará una recarga al proveedor.</p>
          </div>
        </div>
        <dl className="text-sm space-y-2 font-mono">
          {[
            ["Pedido", operation.order_number ? `#${String(operation.order_number).padStart(4, "0")} · ${operation.order_id}` : operation.order_id],
            ["Jugador", operation.player_id_masked],
            ["Producto", operation.commercial_product],
            ["Proveedor", operation.provider_label],
            ["Plan proveedor", (operation.provider_plan?.steps || []).map((s) => s.provider_package).join(" + ") || "—"],
            ["Costo estimado", paMoney(operation.estimated_cost)],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-[var(--ag7-border-soft)] pb-1">
              <dt className="text-[var(--ag7-text-muted)] text-xs uppercase tracking-wider">{k}</dt>
              <dd className="text-white text-right">{v || "—"}</dd>
            </div>
          ))}
        </dl>
        <div className="flex gap-2 pt-1">
          <button onClick={onCancel} data-testid="pa-confirm-cancel" disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--ag7-border)] text-zinc-300 text-sm font-bold hover:bg-ag7-surface-2 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={onConfirm} data-testid="pa-confirm-execute" disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-sm font-black inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Rocket className="w-4 h-4" />
            {busy ? "Ejecutando…" : "Confirmar y ejecutar"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* Detalle de una operación: plan, timeline, evidencia y auditoría. */
export const PaDetailsModal = ({ operation, onClose }) => {
  if (!operation) return null;
  const reached = new Set(["CREATED", "READY_FOR_PROVIDER"]);
  if (operation.executed_at) { reached.add("EXECUTING"); }
  if (operation.post_sent) { reached.add("SUBMITTED"); }
  if (operation.reconciliation_result || operation.status === "RECONCILING") { reached.add("RECONCILING"); }
  if (operation.status === "CONFIRMED") { reached.add("CONFIRMED"); }

  return (
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-8 overflow-y-auto"
      data-testid="pa-details-modal">
      <div className="w-full max-w-2xl bg-ag7-surface border border-ag7-purple/30 rounded-2xl p-6 space-y-5 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-black text-white uppercase tracking-wide">Detalle de operación</h3>
            <p className="text-[11px] font-mono text-[var(--ag7-text-muted)]">{operation.operation_id}</p>
          </div>
          <div className="flex items-center gap-3">
            <PaStatusBadge status={operation.status} />
            <button onClick={onClose} data-testid="pa-details-close" className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm font-mono">
          {[
            ["Pedido AG7", operation.order_id],
            ["Usuario", operation.user_email],
            ["UID jugador", operation.player_id_masked],
            ["Nickname", operation.player_nickname],
            ["Producto comercial", operation.commercial_product],
            ["Producto proveedor", operation.provider_product_id],
            ["Costo estimado", paMoney(operation.estimated_cost)],
            ["Costo real", paMoney(operation.actual_cost)],
            ["Creada", paDate(operation.created_at)],
            ["Ejecutada", paDate(operation.executed_at)],
            ["Ejecutada por", operation.executed_by],
            ["Idempotency key", (operation.idempotency_key || "").slice(0, 18) + "…"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 border-b border-[var(--ag7-border-soft)] py-1">
              <span className="text-[10px] uppercase tracking-wider text-[var(--ag7-text-muted)]">{k}</span>
              <span className="text-white text-right break-all">{v || "—"}</span>
            </div>
          ))}
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)] mb-2">Plan de ejecución</p>
          <div className="rounded-xl border border-[var(--ag7-border-soft)] p-3 space-y-1.5" data-testid="pa-details-plan">
            <p className="text-xs text-zinc-300 font-mono">
              {operation.provider_plan?.kind === "COMPOSITE" ? "COMPUESTO" : "DIRECTO"} ·{" "}
              {operation.provider_plan?.total_children} paso(s) · total {paMoney(operation.provider_plan?.total_amount_usd)}
            </p>
            {(operation.provider_plan?.steps || []).map((step) => (
              <p key={step.sequence} className="text-xs font-mono text-zinc-400">
                {step.sequence}. {step.provider_label || step.provider_product_code} · {paMoney(step.expected_amount)}
              </p>
            ))}
            {(operation.provider_plan?.errors || []).map((err) => (
              <p key={err} className="text-xs font-mono text-red-300">⛔ {err}</p>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)] mb-2">Timeline</p>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="pa-details-timeline">
            {PA_TIMELINE?.map((step) => (
              <span key={step}
                className={`px-2 py-1 rounded-lg text-[10px] font-mono uppercase tracking-wider border ${reached.has(step) ? "bg-ag7-purple/20 text-white border-ag7-purple/50" : "text-zinc-500 border-[var(--ag7-border-soft)]"}`}>
                {step}
              </span>
            ))}
          </div>
        </div>

        {(operation.status_reason || operation.provider_control_number || operation.reconciliation_result) && (
          <div className="rounded-xl border border-[var(--ag7-border-soft)] p-3 space-y-1 text-xs font-mono"
            data-testid="pa-details-outcome">
            {operation.status_reason && <p className="text-zinc-300">Motivo: {operation.status_reason}</p>}
            {operation.provider_control_number && <p className="text-emerald-300">Nº de control: {operation.provider_control_number}</p>}
            {operation.provider_transaction_id && <p className="text-emerald-300">Transacción: {operation.provider_transaction_id}</p>}
            {operation.reconciliation_result && (
              <p className="text-orange-200">
                Conciliación: {operation.reconciliation_result}
                {operation.reconciliation_score != null ? ` (${operation.reconciliation_score}/100)` : ""}
              </p>
            )}
          </div>
        )}

        {operation.price_check && (
          <div className="rounded-xl border border-[var(--ag7-border-soft)] p-3 space-y-1 text-xs font-mono"
            data-testid="pa-details-price-check">
            <p className={operation.price_check.result === "MATCH" ? "text-emerald-300" : "text-orange-300"}>
              {operation.price_check.result === "MATCH" ? "✅" : "⚠️"} Precio: {operation.price_check.reason}
            </p>
            {(operation.price_check.items || []).map((item) => (
              <p key={item.sequence} className="text-zinc-400">
                {item.provider_package}: esperado {paMoney(item.expected_price_usd)} · proveedor{" "}
                {item.live_price_usd != null ? paMoney(item.live_price_usd) : "—"}
              </p>
            ))}
          </div>
        )}

        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)] mb-2">Auditoría</p>
          <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="pa-details-audit">
            {(operation.audit_trail || []).map((entry, idx) => (
              <p key={idx} className="text-[11px] font-mono text-zinc-400">
                <span className="text-zinc-500">{paDate(entry.at)}</span> · {entry.event} · {entry.detail}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
